import styled from '@emotion/styled'
import { Skeleton, SkeletonProps } from '@mui/material'
import { FC } from 'react'
import { Colors, Shapes } from 'src/components/theme'

const COLORS = {
  dark: Colors.neutral300,
  medium: Colors.neutral200,
  normal: Colors.neutral100,
  light: Colors.neutral50,
}
type LoaderColor = keyof typeof COLORS

type LoaderProps = SkeletonProps & {
  color?: LoaderColor
}

export const GenericLoader = styled(Skeleton)<LoaderProps>`
  background-color: ${({ color }) => COLORS[color || 'normal']};
  &.MuiSkeleton-rounded {
    border-radius: ${(props) => {
      if (!props.height) {
        return '0px'
      }
      if (typeof props.height === 'number') {
        return props.height >= 32 ? `${Shapes.borderRadius}px` : '2px'
      }
      return '2px'
    }};
  }
`

type FlexProps = {
  gap?: number
  padding?: number
  justifyContent?: 'center' | 'flex-start' | 'flex-end' | 'space-between' | 'space-around' | 'space-evenly'
  alignItems?: 'center' | 'flex-start' | 'flex-end' | 'baseline' | 'stretch'
}

export const LoaderCol = styled.div<FlexProps>`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  gap: ${(props) => props.gap || Shapes.spacing[2]}px;
  justify-content: ${(props) => props.justifyContent || 'flex-start'};
  align-items: ${(props) => props.alignItems || 'stretch'};
`

export const LoaderRow = styled.div<FlexProps>`
  display: flex;
  flex-direction: row;
  flex-shrink: 0;
  gap: ${(props) => props.gap || Shapes.spacing[2]}px;
  justify-content: ${(props) => props.justifyContent || 'flex-start'};
  align-items: ${(props) => props.alignItems || 'flex-start'};
  padding: ${(props) => props.padding || 0}px;
`

const LoaderRowItem = styled(LoaderRow)`
  border: 1px solid ${Colors.neutral100};
  padding: ${(props) => props.padding || 16}px;
  width: 100%;
  border-radius: 6px;
`

export const SidebarResultOverlayHeaderLoading = () => {
  return (
    <LoaderRow alignItems="center">
      <GenericLoader color="dark" variant="circular" width={32} height={32} animation="wave" />
      <LoaderCol>
        <GenericLoader color="medium" variant="rounded" width={190} height={20} animation="wave" />
        <GenericLoader variant="rounded" width={165} height={12} animation="wave" />
      </LoaderCol>
    </LoaderRow>
  )
}

export const SidebarResultOverlayItemLoading = () => {
  return (
    <LoaderCol>
      <GenericLoader variant="rounded" width={40} height={12} animation="wave" />
      <GenericLoader color="medium" variant="rounded" width={70} height={16} animation="wave" />
    </LoaderCol>
  )
}

export const TitleLoader = () => {
  return <GenericLoader color="medium" variant="rounded" width={85} height={12} animation="wave" />
}

export const BoxLoader = () => {
  return <GenericLoader color="light" variant="rounded" width={'100%'} height={140} animation="wave" />
}

export const RowLoader = () => {
  return (
    <LoaderCol
      style={{
        flex: 1,
      }}>
      <LoaderRow alignItems="center">
        <LoaderCol
          style={{
            flex: 1,
          }}>
          <LoaderRow alignItems="center">
            <GenericLoader color="dark" variant="circular" width={10} height={10} animation="wave" />
            <GenericLoader variant="rounded" width={32} height={10} animation="wave" />
          </LoaderRow>
        </LoaderCol>
        <LoaderCol
          style={{
            flex: 1,
          }}>
          <GenericLoader variant="rounded" width={60} height={10} animation="wave" />
        </LoaderCol>
        <LoaderCol
          style={{
            flex: 1,
          }}>
          <GenericLoader color="dark" variant="text" width={100} height={12} animation="wave" />
        </LoaderCol>
      </LoaderRow>
      <GenericLoader variant="rectangular" width={'100%'} height={1} animation="wave" />
    </LoaderCol>
  )
}

export const SidebarResultItemLoader = () => (
  <LoaderRowItem>
    <LoaderCol>
      <GenericLoader color="medium" variant="circular" width={40} height={40} animation="wave" />
    </LoaderCol>
    <LoaderCol>
      <GenericLoader variant="rounded" width={57} height={12} animation="wave" color="normal" />
      <GenericLoader variant="rounded" width={186} height={20} animation="wave" color="medium" />
      <GenericLoader variant="rounded" width={162} height={12} animation="wave" />
      <LoaderRow alignItems="center">
        <GenericLoader variant="rounded" width={12} height={12} animation="wave" color="medium" />
        <GenericLoader variant="rounded" width={18} height={8} animation="wave" color="normal" />
        <GenericLoader variant="rounded" width={12} height={12} animation="wave" color="medium" />
        <GenericLoader variant="rounded" width={18} height={8} animation="wave" color="normal" />
        <GenericLoader variant="rounded" width={89} height={8} animation="wave" color="normal" />
      </LoaderRow>
    </LoaderCol>
  </LoaderRowItem>
)

type SidebarResultListLoaderProps = {
  numberOfItems?: number
}
export const SidebarResultListLoader: FC<SidebarResultListLoaderProps> = ({ numberOfItems = 6 }) => {
  return (
    <>
      {[...Array(numberOfItems)].map((_, index) => (
        <SidebarResultItemLoader key={`SidebarResultItemLoader-${index}`} />
      ))}
    </>
  )
}

export const ParcelOwnerLoader = () => (
  <LoaderRowItem alignItems="center" padding={10}>
    <LoaderCol>
      <GenericLoader color="medium" variant="circular" width={24} height={24} animation="wave" />
    </LoaderCol>
    <LoaderCol>
      <GenericLoader variant="rounded" width={123} height={12} animation="wave" color="medium" />
      <GenericLoader variant="rounded" width={280} height={10} animation="wave" color="normal" />
    </LoaderCol>
  </LoaderRowItem>
)
